import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  RendererStyleFlags2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SiaBaseScriptComponent, ThemeService } from '@sia/shared';
import { format } from 'date-fns';
import { formatNumber } from 'devextreme/localization';
import { SiaTemplateConfiguration } from '../../interfaces/template-configuration';
import { SiaTemplateService } from '../../services/template.service';

@Component({
  selector: 'sia-template-renderer',
  standalone: true,
  imports: [],
  template: ``,
  styleUrl: './template-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaTemplateRendererComponent extends SiaBaseScriptComponent {
  readonly template = input<SiaTemplateConfiguration>();
  readonly dragging = input<boolean>(false);
  readonly data = input<any>();

  readonly #templateService = inject(SiaTemplateService); //Usato per i template-x
  readonly #el = inject(ElementRef<HTMLElement>);
  readonly #themeService = inject(ThemeService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #renderer = inject(Renderer2);

  editorVisible = true;
  nativeElement!: HTMLElement;

  #originalStyles = new WeakMap<HTMLElement, string>();
  #conditionalClasses = new WeakMap<HTMLElement, string[]>();

  // Precomputed lowercase data — avoids repeated re-keying
  readonly dataLowerCase = computed(() => {
    const src = this.data();
    if (!src) return {};
    const result: Record<string, any> = {};
    for (const key of Object.keys(src)) {
      result[key.toLowerCase()] = src[key];
    }
    return result;
  });
  #previousDataJson: string = '';
  #previousTemplateJson: string = '';

  constructor() {
    super();
    this.nativeElement = this.#el.nativeElement;

    // Re-render when template or data changes, skip if dragging
    effect(() => {
      const template = this.template();
      const data = this.data();
      let currentDataJson = JSON.stringify(data);
      let currentTemplateJSON = JSON.stringify(template);
      if (
        currentDataJson == this.#previousDataJson &&
        currentTemplateJSON == this.#previousTemplateJson
      ) {
        return;
      }
      this.#previousDataJson = currentDataJson;
      this.#previousTemplateJson = currentTemplateJSON;
      if (template && data && !this.dragging()) {
        this.renderTemplate();
      }
    });

    // Theme changes also skip re-render while dragging
    this.#themeService.themeChanged
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (!this.dragging()) {
          this.renderTemplate();
        }
      });
  }

  async renderTemplate(): Promise<void> {
    const tmpl = this.template();
    if (!tmpl?.html || !this.#el?.nativeElement) return;

    const host = this.#el.nativeElement as HTMLElement;
    const lc = this.dataLowerCase();

    // ── 1. Inject HTML ───────────────────────────────────────────────────────
    host.innerHTML = tmpl.html;
    this.nativeElement = host.firstChild as HTMLElement;

    // ── 2. Move custom-class → class IMMEDIATELY, before any async work ──────
    //    This ensures styles are applied on the very first paint.
    this.applyCustomClasses(host);

    // ── 3. Apply colorContrast IMMEDIATELY too ───────────────────────────────
    this.applyColorContrastAll(host);

    // ── 4. Run conditional CSS scripts in parallel ───────────────────────────
    const cssPromises: Promise<void>[] = [];
    for (const condition of tmpl.cssConditions ?? []) {
      if (!condition.elementId || !condition.script) continue;
      const elements = host.querySelectorAll<HTMLElement>(
        `#${condition.elementId}`,
      );
      for (const element of Array.from(elements)) {
        if (!element?.style) continue;

        if (!this.#originalStyles.has(element)) {
          this.#originalStyles.set(element, element.style.cssText);
        }
        element.style.cssText = this.#originalStyles.get(element) ?? '';

        const prevClasses = this.#conditionalClasses.get(element) ?? [];
        for (const cls of prevClasses) element.classList.remove(cls);
        this.#conditionalClasses.set(element, []);

        cssPromises.push(
          this.execScript(condition.script!, lc).then((applyStyle) => {
            if (!applyStyle) return;
            if (condition.property === 'style') {
              const resolved = (condition.value ?? '').replace(
                /\[([^\]]+)\]/g,
                (_, p1) => lc[p1] ?? _,
              );
              element.style.cssText += ' ' + resolved;
            } else if (condition.property === 'class') {
              element.classList.add(condition.value!);
              this.#conditionalClasses.set(element, [
                ...(this.#conditionalClasses.get(element) ?? []),
                condition.value!,
              ]);
            }
          }),
        );
      }
    }

    // ── 5. Inject data fields in parallel ───────────────────────────────────
    const dataPromises: Promise<void>[] = [];
    for (const data of tmpl.dataToInject ?? []) {
      if (!data.elementId || (!data.dataField && !data.dataScript)) continue;

      const elements = host.querySelectorAll<HTMLElement>(`#${data.elementId}`);
      if (!elements.length) continue;

      dataPromises.push(
        (async () => {
          let value: any;
          if (data.useScript && data.dataScript) {
            value = await this.execScript(data.dataScript, lc);
          } else {
            value = lc[data.dataField!.toLowerCase()];
          }

          if (value == null) {
            for (const el of Array.from(elements)) {
              if (el?.style) el.style.cssText += ' display: none;';
            }
            return;
          }

          // Pipes
          if (data.pipe) {
            switch (data.pipe) {
              case 'date':
                value = format(value, data.format || 'dd/MM/yyyy');
                break;
              case 'currency':
                value = new Intl.NumberFormat('it-IT', {
                  style: 'currency',
                  currency: 'EUR',
                }).format(value);
                break;
              case 'number':
                value = formatNumber(value, data.format || '#,###.##');
                break;
            }
          }

          if (data.canTranslate) {
            value = this.i18nService.translate(value);
          }

          for (const el of Array.from(elements)) {
            if (data.attribute) {
              if (data.attribute === 'src' || data.attributeIsProperty) {
                this.#renderer.setProperty(el, data.attribute, value);
              } else {
                this.#renderer.setAttribute(el, data.attribute, value);
              }
            } else {
              el.innerHTML = value;
            }
          }
        })(),
      );
    }

    // ── 6. Attach event handlers ─────────────────────────────────────────────
    for (const eh of tmpl.eventHandlers ?? []) {
      if (!eh.elementId || !eh.event || !eh.script) continue;
      const elements = host.querySelectorAll<HTMLElement>(`#${eh.elementId}`);
      for (const element of Array.from(elements)) {
        if (!element?.style) continue;

        if (eh.event === 'click' || eh.event === 'dblclick') {
          element.style.cssText += ' cursor: pointer;';
        }

        const handlerKey = '__event_' + eh.event;
        const oldHandler = (element as any)[handlerKey];
        const listenerOptions = { passive: !eh.preventDefault };
        if (oldHandler) element.removeEventListener(eh.event, oldHandler);

        const handler = ($event: Event) => {
          if (this.dragging()) return;
          if (eh.preventDefault) {
            $event.preventDefault();
            $event.stopPropagation();
          }
          this.execScript(eh.script!, lc);
        };

        (element as any)[handlerKey] = handler;
        element.addEventListener(eh.event, handler, listenerOptions);
      }
    }

    // ── 7. Wait for all async scripts ────────────────────────────────────────
    await Promise.all([...cssPromises, ...dataPromises]);

    // ── 8. Ricalcola il contrasto colore dopo le cssConditions ───────────────
    // Il colorContrast viene applicato anche al punto 3 per evitare flickering,
    // ma le cssConditions possono cambiare il colore di sfondo degli elementi
    // (es. in dark mode), rendendo il colore del testo calcolato inizialmente
    // illeggibile. Rieseguirlo qui garantisce il contrasto corretto.
    this.applyColorContrastAll(host);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Migrate custom-class → class in a single synchronous DOM pass.
   * Called BEFORE any awaits so classes are visible on first paint.
   */
  private applyCustomClasses(host: HTMLElement): void {
    // Use attribute selector for a focused, fast query
    const elements = host.querySelectorAll<Element>('[custom-class]');
    for (const el of Array.from(elements)) {
      const customClass = el.getAttribute('custom-class');
      if (!customClass) continue;
      const current = el.getAttribute('class');
      this.#renderer.setAttribute(
        el,
        'class',
        current ? `${current} ${customClass}` : customClass,
      );
      this.#renderer.removeAttribute(el, 'custom-class');
    }
  }

  private applyColorContrastAll(host: HTMLElement): void {
    const selector =
      '[colorcontrastAuto],[colorContrastAuto],[colorcontrastauto]';
    const elements = host.querySelectorAll<HTMLElement>(selector);
    for (const el of Array.from(elements)) {
      this.applyColorContrast(el);
    }
  }

  private applyColorContrast(element: HTMLElement): void {
    let bgColor = getComputedStyle(element).backgroundColor;
    if (!bgColor || bgColor === 'rgba(0, 0, 0, 0)') {
      bgColor = getComputedStyle(element.parentElement!).background;
    }
    this.#renderer.setStyle(
      element,
      'color',
      this.getContrastTextColor(bgColor),
      RendererStyleFlags2.Important,
    );
  }

  private getContrastTextColor(colorStr: string): string {
    const rgb = this.parseColor(colorStr);
    return this.calculateLuminance(rgb) > 0.179 ? '#000000' : '#ffffff';
  }

  private calculateLuminance(rgb: number[]): number {
    const [r, g, b] = rgb.map((c) => {
      const n = c / 255;
      return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private parseColor(colorStr: string): number[] {
    if (colorStr.startsWith('#')) return this.hexToRgb(colorStr);
    const m = colorStr.match(/(\d+),\s*(\d+),\s*(\d+)/);
    return m ? [+m[1], +m[2], +m[3]] : [0, 0, 0];
  }

  private hexToRgb(hex: string): number[] {
    hex = hex.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r, g, b) => r + r + g + g + b + b,
    );
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return r
      ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)]
      : [0, 0, 0];
  }

  override ngOnDestroy(): void {
    // Remove all tracked event listeners
    try {
      const all = this.#el?.nativeElement?.querySelectorAll('*');
      all?.forEach((el: any) => {
        for (const key of Object.keys(el)) {
          if (key.startsWith('__event_')) {
            const event = key.replace('__event_', '');
            try {
              el.removeEventListener(event, el[key]);
              delete el[key];
            } catch {
              /* ignore */
            }
          }
        }
      });
    } catch {
      /* ignore */
    }

    this.#originalStyles = new WeakMap();
    this.#conditionalClasses = new WeakMap();
    this.nativeElement = null as any;

    try {
      while (this.#el?.nativeElement?.firstChild) {
        this.#el.nativeElement.removeChild(this.#el.nativeElement.firstChild);
      }
    } catch {
      /* ignore */
    }

    super.ngOnDestroy();
  }
}
